import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/verify", label: "Verify" },
  { to: "/certificate", label: "The Certificate" },
  { to: "/services", label: "Services" },
  { to: "/industry", label: "For Industry" },
  { to: "/buyers", label: "For Buyers" },
  { to: "/journey", label: "The Botswana Journey" },
  { to: "/trust", label: "Trust & Governance" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
    // Scroll listener is self-contained; attach once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <header
      data-testid="main-nav"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass-strong border-b border-white/5 py-3" : "py-5 bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
          <img src="/logo-mark.png" alt="" className="h-11 w-auto transition-transform duration-500 group-hover:scale-105" />
          <div className="leading-none">
            <div className="font-serif text-lg tracking-[0.18em] text-bb-white">BOTSWANA</div>
            <div className="label-eyebrow -mt-0.5">Brilliance</div>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `text-[0.82rem] tracking-wide transition-colors duration-300 hover:text-bb-gold ${
                  isActive ? "text-bb-gold" : "text-bb-silver/80"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-3">
          {user ? (
            <button
              data-testid="nav-portal-btn"
              onClick={() => navigate("/portal")}
              className="btn-gold rounded-full px-5 py-2 text-sm"
            >
              Dashboard
            </button>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-btn" className="btn-ghost rounded-full px-5 py-2 text-sm">
                Industry Login
              </Link>
              <Link to="/verify" data-testid="nav-verify-btn" className="btn-gold rounded-full px-5 py-2 text-sm">
                Verify a Diamond
              </Link>
            </>
          )}
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="xl:hidden text-bb-white"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? <X strokeWidth={1.2} /> : <Menu strokeWidth={1.2} />}
        </button>
      </div>

      {open && (
        <div className="xl:hidden glass-strong border-t border-white/5 mt-3 px-6 py-6 space-y-4">
          {LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className="block text-bb-silver/90 tracking-wide"
            >
              {l.label}
            </Link>
          ))}
          <div className="flex gap-3 pt-2">
            <Link to="/login" onClick={() => setOpen(false)} className="btn-ghost rounded-full px-5 py-2 text-sm flex-1 text-center">
              Login
            </Link>
            <Link to="/verify" onClick={() => setOpen(false)} className="btn-gold rounded-full px-5 py-2 text-sm flex-1 text-center">
              Verify
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
